import type { ChatWidgetConfig } from './types';
import { DEFAULT_CONFIG } from './types';

export function mergeConfig(userConfig: Partial<ChatWidgetConfig>): ChatWidgetConfig {
  return {
    version: userConfig.version || DEFAULT_CONFIG.version || '1.0.0',
    apiUrl: userConfig.apiUrl || import.meta.env.VITE_API_URL || 'https://staging-ai-api.vinfotech.com/api',
    chatEndpoint: userConfig.chatEndpoint || DEFAULT_CONFIG.chatEndpoint || 'sales-bot',
    userId: userConfig.userId || 'default_user',
    teamId: userConfig.teamId || 'default_team',
    theme: {
      ...DEFAULT_CONFIG.theme,
      ...userConfig.theme
    },
    branding: {
      ...DEFAULT_CONFIG.branding,
      ...userConfig.branding
    },
    behavior: {
      ...DEFAULT_CONFIG.behavior,
      ...userConfig.behavior
    },
    messages: {
      ...DEFAULT_CONFIG.messages,
      ...userConfig.messages
    },
    suggestions: {
      ...DEFAULT_CONFIG.suggestions,
      ...userConfig.suggestions
    },
    customization: userConfig.customization,
    callbacks: userConfig.callbacks
  } as ChatWidgetConfig;
}

export function detectTheme(_mode?: 'light' | 'dark' | 'auto'): 'light' | 'dark' {
  // Forced to light mode as per user request
  return 'light';
}

export function getSessionId(storageKey: string = 'vinfotech_chat_session'): string {
  if (typeof window === 'undefined') {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

export function resetSessionId(storageKey: string = 'vinfotech_chat_session'): string {
  const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(storageKey, newSessionId);
      localStorage.setItem(storageKey, newSessionId);
    } catch {}
  }
  return newSessionId;
}


export function validateConfig(config: Partial<ChatWidgetConfig>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!config.apiUrl) {
    errors.push('apiUrl is required');
  }

  if (config.apiUrl && !isValidUrl(config.apiUrl)) {
    errors.push('apiUrl must be a valid URL');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export function hexToRgba(hex: string, alpha: number): string {
  const match = hex.trim().match(/^#?([a-f\d]{3}|[a-f\d]{6})$/i);

  if (!match) {
    return `rgba(103, 32, 255, ${alpha})`;
  }

  let value = match[1];
  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('');
  }

  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch {
    return false;
  }
}

export function injectStyles(css: string, id: string = 'vinfotech-widget-custom-styles') {
  if (typeof document === 'undefined') return;

  let styleEl = document.getElementById(id) as HTMLStyleElement;

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = id;
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = css;
}

export function removeStyles(id: string = 'vinfotech-widget-custom-styles') {
  if (typeof document === 'undefined') return;

  const styleEl = document.getElementById(id);
  if (styleEl) {
    styleEl.remove();
  }
}

/**
 * Normalizes markdown content to ensure proper formatting of bullet points.
 * This fixes inconsistent newline handling from AI responses where bullet points
 * may not have proper line breaks between them.
 * 
 * Handles:
 * - Unicode bullet character (•)
 * - Standard markdown bullets (-, *, +)
 * - Ensures each bullet point is on its own line
 * - Converts • to - for proper markdown list rendering
 * 
 * Safety:
 * - Preserves mathematical equations inside $...$ and $$...$$ delimiters
 * - Only treats characters as bullets when they appear in bullet-like contexts
 */
export function normalizeMarkdownBullets(text: string): string {
  if (!text) return text;

  // Protect math expressions by temporarily replacing them with placeholders
  const mathPlaceholders: string[] = [];

  // Protect display math $$...$$ first (must come before inline math)
  let normalized = text.replace(/\$\$[\s\S]*?\$\$/g, (match) => {
    mathPlaceholders.push(match);
    return `__MATH_PLACEHOLDER_${mathPlaceholders.length - 1}__`;
  });

  // Protect inline math $...$
  normalized = normalized.replace(/\$[^$\n]+?\$/g, (match) => {
    mathPlaceholders.push(match);
    return `__MATH_PLACEHOLDER_${mathPlaceholders.length - 1}__`;
  });

  // Protect code blocks ```...```
  normalized = normalized.replace(/```[\s\S]*?```/g, (match) => {
    mathPlaceholders.push(match);
    return `__MATH_PLACEHOLDER_${mathPlaceholders.length - 1}__`;
  });

  // Protect inline code `...`
  normalized = normalized.replace(/`[^`\n]+?`/g, (match) => {
    mathPlaceholders.push(match);
    return `__MATH_PLACEHOLDER_${mathPlaceholders.length - 1}__`;
  });

  // Now safely process bullet points

  // Handle Unicode bullet (•) followed by bold text - ensure newline before it
  // Pattern: non-newline char followed by • and then ** (bold)
  normalized = normalized.replace(/([^\n])(•)\s*\*\*/g, '$1\n$2 **');

  // Convert • to - for proper markdown rendering
  normalized = normalized.replace(/•/g, '-');

  // Handle standard markdown bullets at line start that need spacing (-**Label** or +**Label**)
  // Exclude * so lines starting with *** (bold italic) or ** (bold) are never corrupted
  normalized = normalized.replace(/^([\-+])(?=\s*\*\*)/gm, '$1 ');

  // Ensure there's a newline before bullet points that are stuck to previous content
  // Only when followed by ** (bold) to indicate it's a list item, not math
  // This handles: "...performance- **Next item" -> "...performance\n- **Next item"
  normalized = normalized.replace(/([^\n\s])\s*([\-])\s*(\*\*)/g, '$1\n$2 $3');

  // Clean up any triple+ newlines
  normalized = normalized.replace(/\n{3,}/g, '\n\n');

  // Restore protected math and code expressions
  mathPlaceholders.forEach((math, index) => {
    normalized = normalized.replace(`__MATH_PLACEHOLDER_${index}__`, math);
  });

  return normalized;
}

/**
 * While a response is still streaming in, a token like `**` or a fenced
 * code block may have only its opening half so far. CommonMark renders an
 * unmatched delimiter as a literal character, so mid-stream text flashes
 * raw `**`/backtick symbols until the closing half arrives a moment later.
 * This closes any currently-open token so the preview renders formatted
 * immediately; it's a no-op once the real closing token streams in.
 */
export function closeIncompleteMarkdownTokens(text: string): string {
  if (!text) return text;

  const fenceCount = (text.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) {
    // Inside an open code fence: '*' and '`' below are code content, not
    // markdown syntax, so just close the fence and stop.
    return `${text}\n\`\`\``;
  }

  const withoutFences = text.replace(/```[\s\S]*?```/g, '');
  let result = text;

  const backtickCount = (withoutFences.match(/`/g) || []).length;
  if (backtickCount % 2 === 1) {
    result += '`';
  }

  const withoutInlineCode = withoutFences.replace(/`[^`\n]*`/g, '');
  const boldCount = (withoutInlineCode.match(/\*\*/g) || []).length;

  if (boldCount % 2 === 1) {
    result += '**';
  } else {
    const withoutBold = withoutInlineCode.replace(/\*\*/g, '');
    const italicCount = (withoutBold.match(/\*/g) || []).length;
    if (italicCount % 2 === 1) {
      result += '*';
    }
  }

  return hideIncompleteLink(result);
}

/**
 * Hide a markdown link that is still arriving.
 *
 * A link streams in as `[`, then the label, then `](`, then the URL — and each
 * of those states renders badly:
 *
 *   "See [AI Market Creator"          → the bracket shows as literal text
 *   "See [AI Market Creator]("        → "[AI Market Creator](" shows verbatim
 *   "See [Label](https://x.com/ai-ma" → a LIVE link to a truncated URL
 *
 * The last is the worst: for a moment the reader can click a half-written
 * address. So an unterminated link is dropped from the rendered text entirely
 * and reappears, complete, once its closing paren arrives. The typing effect
 * keeps running underneath — only this frame's markdown is trimmed.
 */
function hideIncompleteLink(text: string): string {
  // Last '[' that has no matching ')' after it. Scanning from the end keeps
  // this O(tail) rather than re-parsing the whole message every tick.
  const open = text.lastIndexOf('[');
  if (open === -1) return text;

  const rest = text.slice(open);

  // A complete link: [label](url) — nothing to hide.
  if (/^\[[^\]]*\]\([^)\s]*\)/.test(rest)) return text;

  // Mid-link in any of its unfinished forms.
  if (/^\[[^\]]*$/.test(rest) ||          // [label…
      /^\[[^\]]*\]$/.test(rest) ||        // [label]
      /^\[[^\]]*\]\([^)]*$/.test(rest)) { // [label](url…
    return text.slice(0, open);
  }

  return text;
}

/**
 * Per-recipient campaign links.
 *
 * A campaign can mint one unguessable code per client, and their link is the
 * landing page with that code on it:
 *
 *     https://example.com/prediction-market-software?c=r_8f3a2c9d1e
 *
 * The widget forwards the code with every message so the backend can attribute
 * the whole conversation to that named client rather than to an anonymous IP.
 * It is metadata only — it plays no part in retrieval or ranking.
 */
const CAMPAIGN_REF_PARAM = 'c';
const CAMPAIGN_REF_STORAGE_KEY = 'vinfotech_campaign_ref';

/** Codes are "r_" + 12 hex characters (see the backend's mint_recipient_id). */
const CAMPAIGN_REF_PATTERN = /^r_[0-9a-f]{12}$/i;

/**
 * The campaign code for this visit, or null.
 *
 * Read from the URL on first sight and then remembered for the tab, because
 * the code only appears on the landing URL: a visitor who clicks through to
 * another page, or whose site rewrites the URL client-side, would otherwise
 * stop being attributed halfway through their own conversation.
 *
 * sessionStorage rather than localStorage deliberately — the code should last
 * for this visit, not follow the browser forever and mis-attribute a later
 * unrelated visit to the same client.
 */
export function getCampaignRef(): string | null {
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get(CAMPAIGN_REF_PARAM);
  } catch {
    fromUrl = null;
  }

  if (fromUrl && CAMPAIGN_REF_PATTERN.test(fromUrl)) {
    try {
      window.sessionStorage.setItem(CAMPAIGN_REF_STORAGE_KEY, fromUrl);
    } catch {
      // Private mode or storage disabled — the code still works for this page,
      // it just will not survive navigation. Better than failing the chat.
    }
    return fromUrl;
  }

  try {
    const stored = window.sessionStorage.getItem(CAMPAIGN_REF_STORAGE_KEY);
    return stored && CAMPAIGN_REF_PATTERN.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The page identifier sent as `current_page`.
 *
 * Host + path only: the query string is deliberately excluded so that
 * per-client campaign codes, UTM tags and similar do not fragment one page
 * into many. The campaign code travels separately as `campaign_ref`.
 */
export function getCurrentPageUrl(): string {
  return window.location.host + window.location.pathname;
}

/**
 * Extracts the root/registrable domain from a hostname (e.g. 'blog.vinfotech.com' -> 'vinfotech.com').
 * Handles standard domains as well as common multi-part TLDs (e.g. '.co.uk', '.co.in', '.com.au').
 */
export function getRootDomain(hostname: string): string {
  if (!hostname) return '';
  const cleanHost = hostname.toLowerCase().split(':')[0].trim();
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(cleanHost) || cleanHost === 'localhost') {
    return cleanHost;
  }
  const parts = cleanHost.split('.');
  if (parts.length <= 2) return cleanHost;

  const secondLevelTLDs = ['co', 'com', 'org', 'net', 'gov', 'edu', 'ac', 'io'];
  const isSecondLevel =
    parts.length > 2 &&
    secondLevelTLDs.includes(parts[parts.length - 2]) &&
    parts[parts.length - 1].length === 2;

  if (isSecondLevel && parts.length >= 3) {
    return parts.slice(-3).join('.');
  }
  return parts.slice(-2).join('.');
}

/**
 * Determines whether a URL points to an external/different domain relative to the current host.
 * - Internal / same-domain links (relative URLs, anchors, or matching host/root domain) return `false` (open in same tab).
 * - External / different-domain links return `true` (open in new tab).
 */
export function isExternalLink(
  href: string,
  currentHostname?: string,
  apiUrl?: string
): boolean {
  if (!href || href === '#' || href.startsWith('#')) {
    return false;
  }

  // Relative URLs, anchors, or parameter paths are always internal
  if (/^(\/|\.\/|\.\.\/|[a-zA-Z0-9_\-]+(\/|\?|#|$))/.test(href) && !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) {
    return false;
  }

  // mailto and tel URLs must not open a new blank tab
  if (/^(mailto:|tel:)/i.test(href)) {
    return false;
  }

  try {
    const current = (currentHostname || (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase().split(':')[0].trim();
    const baseOrigin = typeof window !== 'undefined' ? window.location.href : 'http://localhost';
    const parsed = new URL(href, baseOrigin);
    const target = parsed.hostname.toLowerCase().split(':')[0].trim();

    if (!target || target === current) {
      return false;
    }

    const stripWww = (h: string) => h.replace(/^www\./, '');
    if (stripWww(target) === stripWww(current)) {
      return false;
    }

    const currentRoot = getRootDomain(current);
    const targetRoot = getRootDomain(target);
    if (currentRoot && targetRoot && currentRoot === targetRoot) {
      return false;
    }

    // When testing in dev on localhost or 127.0.0.1, match against apiUrl / vinfotech.com
    const isLocalDev = current === 'localhost' || current === '127.0.0.1' || current.endsWith('.local');
    if (isLocalDev) {
      let apiRoot = '';
      if (apiUrl) {
        try {
          apiRoot = getRootDomain(new URL(apiUrl).hostname);
        } catch {
          apiRoot = '';
        }
      }
      const defaultRoot = apiRoot || 'vinfotech.com';
      if (targetRoot === defaultRoot) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}


/**
 * Find how much of a streaming markdown document has *settled* — i.e. the
 * length of the leading span whose rendering can no longer change, no matter
 * what text arrives next.
 *
 * Re-parsing the whole message every animation frame is what makes a long
 * reply stutter: remark's cost grows with document length, so by ~3000 chars a
 * single parse can exceed the 16.7ms frame budget on its own, and the React
 * tree for every finished paragraph is rebuilt and diffed alongside it just to
 * produce identical output. Splitting at a settled boundary lets the caller
 * memoize the prefix and re-render only the tail, so per-frame cost tracks the
 * size of the tail instead of the size of the whole answer.
 *
 * Returns a character offset rather than the substring so the caller can
 * enforce monotonicity (the offset must never move backwards, or already
 * painted text would rewrite itself on screen).
 *
 * Conservative by design; it returns 0 — nothing settled — whenever the
 * trailing construct could still absorb what follows:
 *   - inside an unterminated code fence, where a blank line is content
 *   - a trailing list, whose next item would otherwise start a second list
 *   - a trailing table, which grows by rows
 */
export function settledMarkdownLength(text: string): number {
  if (!text) return 0;

  // An open code fence swallows blank lines: nothing is settled until it closes.
  if (((text.match(/```/g) || []).length) % 2 === 1) return 0;

  // Walk block separators from the end. The last boundary whose following
  // block cannot still absorb new text is the settled point.
  const sep = /\n[ \t]*\n/g;
  const cuts: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = sep.exec(text)) !== null) cuts.push(m.index + m[0].length);

  for (let i = cuts.length - 1; i >= 0; i--) {
    const block = text.slice(cuts[i]);
    // A list or table at the tail keeps growing; back up to an earlier cut.
    if (/^[ \t]*(?:[-*+]|\d+[.)])\s/.test(block)) continue;
    if (/^[ \t]*\|/.test(block)) continue;
    return cuts[i];
  }

  return 0;
}

/**
 * Normalizes question strings from qualification payloads or suggestions.
 * Strips leading numbering (e.g. "11. ") and trailing select hints (e.g. " (single select) AMM · Order book").
 */
export function cleanQuestionText(raw: string): string {
  if (!raw) return '';
  let q = raw.trim();
  // Strip leading numbering like "11. " or "1) "
  q = q.replace(/^\d+[\.\)]\s*/, '');
  // Strip trailing select hints or option summaries:
  // e.g. " (single select) AMM · Order book" or " (multi select)" or " (single choice)"
  q = q.replace(/\s*\((single|multi)[-\s]*(select|choice)\).*$/i, '');
  q = q.replace(/\s*\(choose\s+(one|any|all)\).*$/i, '');
  const qMarkIdx = q.lastIndexOf('?');
  if (qMarkIdx !== -1 && qMarkIdx < q.length - 1) {
    const afterQ = q.slice(qMarkIdx + 1).trim();
    if (afterQ.includes('·') || afterQ.includes('|') || afterQ.length > 15) {
      q = q.slice(0, qMarkIdx + 1).trim();
    }
  }
  return q.trim();
}

/**
 * Strips trailing duplicate question from the assistant message prose
 * when a structured qualification card is rendered below it.
 */
export function stripQuestionFromMessage(messageText: string, rawQuestion: string): string {
  if (!messageText || !rawQuestion) return messageText;
  const cleanQ = cleanQuestionText(rawQuestion);
  const text = messageText.trim();
  if (!cleanQ) return messageText;

  if (text === cleanQ) return '';
  if (text.endsWith(cleanQ)) {
    return text.slice(0, text.length - cleanQ.length).trim();
  }

  const boldQ = `**${cleanQ}**`;
  if (text === boldQ) return '';
  if (text.endsWith(boldQ)) {
    return text.slice(0, text.length - boldQ.length).trim();
  }

  const rawTrim = rawQuestion.trim();
  if (text === rawTrim) return '';
  if (rawTrim && text.endsWith(rawTrim)) {
    return text.slice(0, text.length - rawTrim.length).trim();
  }

  // Normalized comparison ignoring spacing/newlines at the tail
  const normCleanQ = cleanQ.toLowerCase().replace(/[\s\n\r]+/g, ' ');
  const normText = text.toLowerCase().replace(/[\s\n\r]+/g, ' ');
  if (normCleanQ && normText.endsWith(normCleanQ)) {
    const idx = text.toLowerCase().lastIndexOf(cleanQ.toLowerCase());
    if (idx !== -1) {
      return text.slice(0, idx).trim();
    }
  }

  return messageText;
}

